import { BASE_COLORS } from '@/lib/baseColors';

/**
 * VT — Organização de Cores. Pure (no-DB) color resolution: normalization, the
 * PT-BR/EN dictionary, the name→hex table and ΔE CIEDE2000 nearest-base match.
 * Kept separate from {@link import('./colorResolver')} so it can be unit-tested
 * and imported without pulling in the Prisma client.
 */

export type ColorResolutionSource =
  | 'store'
  | 'global'
  | 'dictionary'
  | 'name-hex'
  | 'image'
  | 'unmapped';

export type ColorResolution = {
  baseColorId: string | null;
  source: ColorResolutionSource;
};

/**
 * Human-friendly display for a color name: trim, collapse spaces and capitalize
 * the first letter of each word (accents preserved). Fallback for stored names
 * that lack an explicit displayName.
 */
export function formatColorDisplayName(raw: string): string {
  return raw
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) =>
      word
        ? word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1).toLocaleLowerCase('pt-BR')
        : word
    )
    .join(' ');
}

/** Lowercase, trim, collapse spaces, strip accents. Used as the mapping key. */
export function normalizeRawColorName(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// === Dictionary (canonical synonyms) ===

/** Single tokens that map deterministically to a base color. */
const TOKEN_TO_BASE: Record<string, string> = {
  // black
  preto: 'black', preta: 'black', black: 'black', onix: 'black', negro: 'black', negra: 'black',
  // white
  branco: 'white', branca: 'white', white: 'white', neve: 'white',
  // off-white / cru
  cru: 'off-white', crua: 'off-white', ecru: 'off-white', offwhite: 'off-white', off: 'off-white',
  marfim: 'off-white', gelo: 'off-white', perola: 'off-white', creme: 'off-white', ivory: 'off-white',
  // gray
  cinza: 'gray', cinzento: 'gray', gray: 'gray', grey: 'gray', grafite: 'gray', chumbo: 'gray',
  // beige
  bege: 'beige', beige: 'beige', nude: 'beige', areia: 'beige', camel: 'beige', khaki: 'beige',
  caqui: 'beige', tan: 'beige',
  // brown
  marrom: 'brown', brown: 'brown', cafe: 'brown', chocolate: 'brown', caramelo: 'brown',
  castanho: 'brown', tabaco: 'brown', terracota: 'brown', cobre: 'brown', ferrugem: 'brown',
  // red
  vermelho: 'red', vermelha: 'red', red: 'red', vinho: 'red', bordo: 'red', carmim: 'red',
  escarlate: 'red', rubi: 'red', cereja: 'red', maroon: 'red', crimson: 'red',
  // pink
  rosa: 'pink', pink: 'pink', salmao: 'pink', salmon: 'pink', coral: 'pink', magenta: 'pink',
  fucsia: 'pink', rose: 'pink',
  // orange
  laranja: 'orange', orange: 'orange', tangerina: 'orange', cenoura: 'orange', abobora: 'orange',
  // yellow
  amarelo: 'yellow', amarela: 'yellow', yellow: 'yellow', mostarda: 'yellow', canario: 'yellow',
  // green
  verde: 'green', green: 'green', oliva: 'green', militar: 'green', musgo: 'green',
  esmeralda: 'green', menta: 'green', lima: 'green', limao: 'green',
  // blue
  azul: 'blue', blue: 'blue', marinho: 'blue', navy: 'blue', turquesa: 'blue', ciano: 'blue',
  cyan: 'blue', petroleo: 'blue', jeans: 'blue', denim: 'blue', indigo: 'blue', anil: 'blue',
  celeste: 'blue', royal: 'blue', teal: 'blue',
  // purple
  roxo: 'purple', roxa: 'purple', purple: 'purple', violeta: 'purple', lilas: 'purple',
  lavanda: 'purple', uva: 'purple', ameixa: 'purple', berinjela: 'purple',
  // gold
  dourado: 'gold', dourada: 'gold', gold: 'gold', ouro: 'gold',
  // silver
  prata: 'silver', prateado: 'silver', prateada: 'silver', silver: 'silver', aluminio: 'silver',
  // multicolor / print
  estampado: 'multicolor', estampada: 'multicolor', estampa: 'multicolor', floral: 'multicolor',
  listrado: 'multicolor', listrada: 'multicolor', xadrez: 'multicolor', poa: 'multicolor',
  animal: 'multicolor', print: 'multicolor', multicor: 'multicolor', colorido: 'multicolor',
};

function resolveFromDictionary(normalized: string): string | null {
  if (!normalized) return null;

  const direct = TOKEN_TO_BASE[normalized];
  if (direct) return direct;

  const tokens = normalized.split(' ');
  const found = new Set<string>();
  for (const token of tokens) {
    const base = TOKEN_TO_BASE[token];
    if (base) found.add(base);
  }
  if (found.size === 1) return [...found][0];
  // Ambiguous multi-color description (e.g. "azul e rosa") → multicolor.
  if (found.size > 1) return 'multicolor';
  return null;
}

// === name → hex (literal hex or CSS keyword) ===

const CSS_KEYWORD_HEX: Record<string, string> = {
  aliceblue: '#f0f8ff', aqua: '#00ffff', aquamarine: '#7fffd4', azure: '#f0ffff',
  beige: '#f5f5dc', bisque: '#ffe4c4', black: '#000000', blue: '#0000ff',
  brown: '#a52a2a', burlywood: '#deb887', cadetblue: '#5f9ea0', chartreuse: '#7fff00',
  chocolate: '#d2691e', coral: '#ff7f50', crimson: '#dc143c', cyan: '#00ffff',
  darkblue: '#00008b', darkgreen: '#006400', darkgray: '#a9a9a9', darkorange: '#ff8c00',
  darkred: '#8b0000', firebrick: '#b22222', forestgreen: '#228b22', fuchsia: '#ff00ff',
  gold: '#ffd700', goldenrod: '#daa520', gray: '#808080', green: '#008000',
  hotpink: '#ff69b4', indigo: '#4b0082', ivory: '#fffff0', khaki: '#f0e68c',
  lavender: '#e6e6fa', lightblue: '#add8e6', lightgray: '#d3d3d3', lime: '#00ff00',
  magenta: '#ff00ff', maroon: '#800000', mintcream: '#f5fffa', navy: '#000080',
  olive: '#808000', orange: '#ffa500', orangered: '#ff4500', orchid: '#da70d6',
  pink: '#ffc0cb', plum: '#dda0dd', purple: '#800080', red: '#ff0000',
  royalblue: '#4169e1', salmon: '#fa8072', sienna: '#a0522d', silver: '#c0c0c0',
  skyblue: '#87ceeb', slategray: '#708090', tan: '#d2b48c', teal: '#008080',
  tomato: '#ff6347', turquoise: '#40e0d0', violet: '#ee82ee', wheat: '#f5deb3',
  white: '#ffffff', yellow: '#ffff00',
};

/** Parse `#rgb`/`#rrggbb` or a CSS keyword (after normalization) into [r,g,b]. */
function nameToRgb(normalized: string): [number, number, number] | null {
  const compact = normalized.replace(/\s+/g, '');
  const hexCandidate = compact.startsWith('#') ? compact : `#${compact}`;
  const hex = parseHex(hexCandidate);
  if (hex) return hex;
  const keyword = CSS_KEYWORD_HEX[compact];
  if (keyword) return parseHex(keyword);
  return null;
}

export function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let body = m[1];
  if (body.length === 3) body = body.split('').map((c) => c + c).join('');
  const n = parseInt(body, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// === sRGB → Lab and CIEDE2000 ===

export type Lab = { L: number; a: number; b: number };

export function rgbToLab([r, g, b]: [number, number, number]): Lab {
  const srgb = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  // sRGB → XYZ (D65)
  const x = srgb[0] * 0.4124 + srgb[1] * 0.3576 + srgb[2] * 0.1805;
  const y = srgb[0] * 0.2126 + srgb[1] * 0.7152 + srgb[2] * 0.0722;
  const z = srgb[0] * 0.0193 + srgb[1] * 0.1192 + srgb[2] * 0.9505;
  const xn = 0.95047, yn = 1.0, zn = 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x / xn), fy = f(y / yn), fz = f(z / zn);
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

/** CIEDE2000 color difference. Lower = more similar. */
export function deltaE2000(c1: Lab, c2: Lab): number {
  const rad = Math.PI / 180;
  const { L: L1, a: a1, b: b1 } = c1;
  const { L: L2, a: a2, b: b2 } = c2;
  const avgL = (L1 + L2) / 2;
  const c1ab = Math.hypot(a1, b1);
  const c2ab = Math.hypot(a2, b2);
  const avgC = (c1ab + c2ab) / 2;
  const g = 0.5 * (1 - Math.sqrt(avgC ** 7 / (avgC ** 7 + 25 ** 7)));
  const a1p = a1 * (1 + g);
  const a2p = a2 * (1 + g);
  const c1p = Math.hypot(a1p, b1);
  const c2p = Math.hypot(a2p, b2);
  const avgCp = (c1p + c2p) / 2;
  const h1p = (Math.atan2(b1, a1p) * (180 / Math.PI) + 360) % 360;
  const h2p = (Math.atan2(b2, a2p) * (180 / Math.PI) + 360) % 360;
  const deltLp = L2 - L1;
  const deltCp = c2p - c1p;
  let dhp = h2p - h1p;
  if (Math.abs(dhp) > 180) dhp -= Math.sign(dhp) * 360;
  const deltHp = 2 * Math.sqrt(c1p * c2p) * Math.sin((dhp / 2) * rad);
  let avgHp = (h1p + h2p) / 2;
  if (Math.abs(h1p - h2p) > 180) avgHp += 180;
  const t =
    1 -
    0.17 * Math.cos((avgHp - 30) * rad) +
    0.24 * Math.cos(2 * avgHp * rad) +
    0.32 * Math.cos((3 * avgHp + 6) * rad) -
    0.2 * Math.cos((4 * avgHp - 63) * rad);
  const sl = 1 + (0.015 * (avgL - 50) ** 2) / Math.sqrt(20 + (avgL - 50) ** 2);
  const sc = 1 + 0.045 * avgCp;
  const sh = 1 + 0.015 * avgCp * t;
  const deltTheta = 30 * Math.exp(-(((avgHp - 275) / 25) ** 2));
  const rc = 2 * Math.sqrt(avgCp ** 7 / (avgCp ** 7 + 25 ** 7));
  const rt = -rc * Math.sin(2 * deltTheta * rad);
  return Math.sqrt(
    (deltLp / sl) ** 2 +
      (deltCp / sc) ** 2 +
      (deltHp / sh) ** 2 +
      rt * (deltCp / sc) * (deltHp / sh)
  );
}

/** Base colors usable as ΔE targets (the multicolor bucket is a gradient, not a tone). */
const BASE_COLOR_LABS: Array<{ id: string; lab: Lab }> = BASE_COLORS.filter(
  (c) => !c.gradient
).map((c) => ({ id: c.id, lab: rgbToLab(parseHex(c.hex) as [number, number, number]) }));

/** Max ΔE for which a name→hex / image match is accepted; beyond it we leave unmapped. */
export const DELTA_E_THRESHOLD = 24;

export function nearestBaseByRgb(rgb: [number, number, number]): { id: string; delta: number } {
  const lab = rgbToLab(rgb);
  let best = BASE_COLOR_LABS[0];
  let bestDelta = Infinity;
  for (const candidate of BASE_COLOR_LABS) {
    const delta = deltaE2000(lab, candidate.lab);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = candidate;
    }
  }
  return { id: best.id, delta: bestDelta };
}

/** Resolve a normalized name via dictionary first, then name→hex ΔE. */
export function resolveFromText(normalized: string): ColorResolution | null {
  const dict = resolveFromDictionary(normalized);
  if (dict) return { baseColorId: dict, source: 'dictionary' };

  const rgb = nameToRgb(normalized);
  if (rgb) {
    const nearest = nearestBaseByRgb(rgb);
    if (nearest.delta <= DELTA_E_THRESHOLD) {
      return { baseColorId: nearest.id, source: 'name-hex' };
    }
  }
  return null;
}

/** Resolve a raw color name through the pure cascade (no store/global overrides). */
export function resolveColorNameToBase(rawName: string): ColorResolution {
  const normalized = normalizeRawColorName(rawName);
  if (!normalized) return { baseColorId: null, source: 'unmapped' };
  return resolveFromText(normalized) ?? { baseColorId: null, source: 'unmapped' };
}
